import React, { useState, useEffect } from 'react';
import { Button, MenuItem, Select, FormControl, InputLabel, Dialog, DialogActions, DialogContent, DialogTitle, TextField, CircularProgress, Autocomplete } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from '@mui/icons-material/Business';
import { useNavigate } from 'react-router-dom';
import { local_url } from '../Const';
import Header from '../Header';
import { useLocation } from 'react-router-dom';
import CompanyList from './CompanyList';

export const BranchList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyName, companyCode } = location.state || {};
  const [ledgerData, setLedgerData] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // New state for dropdown options
  const [placeOptions, setPlaceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([
    { code: 'INMH', name: 'Maharashtra' },
    { code: 'INCT', name: 'Chhattisgarh' }
  ]);
  const [beatOptions, setBeatOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);

  const [formData, setFormData] = useState({
    branchID: "",
    forBranchID: "",
    branchCode: "",
    branchName: "",
    creditLimit: 0,
    creditDays: 0,
    enhancedCreditLimitPC: 0,
    enhancedCreditLimitDays: 0,
    beatID: "",
    branchType: "",
    entityCode: "",
    stateCode: "",
    gstNumber: "",
    rtype: "",
    zoneCode: "",
    createdUserId: "",
    mobile: "",
    houseNo: "",
    building: "",
    branchAddress: "",
    landmark: "",
    cityCode: "",
    placeCode: "",
    placeName: "",
    isDistanceEdited: false,
    kmDistance: 0,
    pinCode: "",
    altPhone: "",
    email: "",
    foodLicenseNumber: "",
    latitude: 0,
    longitude: 0,
    createdDate: "",
    modifiedUserId: "",
    modifiedDate: "",
    deletedUserId: "",
    deletedDate: "",
    // Additional fields from branch detail API
    isDefaultAddress: false,
    addressID: ""
  });

  const [formErrors, setFormErrors] = useState({});

  // Mandatory fields list
  const mandatoryFields = [
    'branchID',
    'forBranchID', 
    'branchCode',
    'branchName',
    'mobile',
    'houseNo',
    'building',
    'branchAddress',
    'landmark',
    'cityCode',
    'placeCode',
    'placeName',
    'pinCode'
  ];

  // Branch type options
  const branchTypeOptions = [
    { value: 'ST', label: 'On Branch' },
    { value: 'CS', label: 'Customer' },
    { value: 'LV', label: 'Local Vendor' }
  ];

  // RType options
  const rtypeOptions = [
    { value: 'LAZIZ', label: 'LAZIZ' },
    { value: 'RETAIL', label: 'Retail' }
  ];

  // Fetch places by pin code
  const fetchPlacesByPinCode = async (pinCode) => {
    if (!pinCode || pinCode.length < 6) {
      setPlaceOptions([]);
      return;
    }

    setLoadingPlaces(true);
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`https://ibretailapi.abisaio.com/api/Place/${pinCode}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.places && Array.isArray(data.places)) {
        setPlaceOptions(data.places);
      } else {
        setPlaceOptions([]);
      }
    } catch (error) {
      console.error("Error fetching places:", error);
      setPlaceOptions([]);
    } finally {
      setLoadingPlaces(false);
    }
  };

  // Fetch place details by place ID
  const fetchPlaceDetails = async (placeId) => {
    if (!placeId) return;

    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_url}/api/PlaceName/GetPlaceById/${placeId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.length > 0) {
        const placeData = data[0];
        setFormData(prev => ({
          ...prev,
          cityCode: placeData.cityId,
          stateCode: placeData.stateCode,
          pinCode: placeData.pinCode,
          placeName: placeData.placeName // Update place name from API
        }));
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  // Fetch place name by place code (for editing scenario)
  const fetchPlaceNameByCode = async (placeCode) => {
    if (!placeCode) return "";

    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_url}/api/PlaceName/GetPlaceById/${placeCode}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.length > 0) {
        return data[0].placeName;
      }
      return "";
    } catch (error) {
      console.error("Error fetching place name by code:", error);
      return "";
    }
  };

  const fetchLedgerData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_url}/api/Company/GetBranchList/ALL/NAME/${companyName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setLedgerData(data);
    } catch (error) {
      setError("Failed to fetch branch data. Please try again later.");
      console.error("API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyList = async () => {
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_url}/api/Company/GetCompanyList`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCompanyList(data);
    } catch (error) {
      setError("Failed to fetch company list. Please try again later.");
      console.error("API Error:", error);
    }
  };

  useEffect(() => {
    fetchLedgerData();
    fetchCompanyList();
  }, []);

  // Watch for pin code changes to fetch places
  useEffect(() => {
    if (formData.pinCode && formData.pinCode.length === 6) {
      fetchPlacesByPinCode(formData.pinCode);
    }
  }, [formData.pinCode]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: newValue 
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle place selection
  const handlePlaceSelect = (event, newValue) => {
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        placeCode: newValue.placeId,
        placeName: newValue.placeName
      }));
      
      // Fetch additional place details
      fetchPlaceDetails(newValue.placeId);
    } else {
      setFormData(prev => ({
        ...prev,
        placeCode: "",
        placeName: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    mandatoryFields.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        errors[field] = 'This field is required';
      }
    });

    // Additional validation for mobile (should be 10 digits)
    if (formData.mobile && formData.mobile.length !== 10) {
      errors.mobile = 'Mobile number should be 10 digits';
    }

    // Additional validation for email format if provided
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    // Pin code validation
    if (formData.pinCode && formData.pinCode.length !== 6) {
      errors.pinCode = 'Pin code should be 6 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenForm = () => {
    setIsEditing(false);
    setFormErrors({});
    setPlaceOptions([]);
    setFormData({
      branchID: "",
      forBranchID: "",
      branchCode: "",
      branchName: "",
      creditLimit: 0,
      creditDays: 0,
      enhancedCreditLimitPC: 0,
      enhancedCreditLimitDays: 0,
      beatID: "",
      branchType: "",
      entityCode: "",
      stateCode: "",
      gstNumber: "",
      rtype: "",
      zoneCode: "",
      createdUserId: "",
      mobile: "",
      houseNo: "",
      building: "",
      branchAddress: "",
      landmark: "",
      cityCode: "",
      placeCode: "",
      placeName: "",
      isDistanceEdited: false,
      kmDistance: 0,
      pinCode: "",
      altPhone: "",
      email: "",
      foodLicenseNumber: "",
      latitude: 0,
      longitude: 0,
      createdDate: "",
      modifiedUserId: "",
      modifiedDate: "",
      deletedUserId: "",
      deletedDate: "",
      // Additional fields from branch detail API
      isDefaultAddress: false,
      addressID: ""
    });
    setOpenForm(true);
  };

  // Fetch branch details by branch ID
  const fetchBranchDetails = async (branchID) => {
    if (!branchID) return;

    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_url}/api/company/getbranchdetail/${branchID}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.length > 0) {
        // Get the first record or the one with default address
        const branchData = data.find(item => item.isDefaultAddress) || data[0];
        
        // Fetch place name if place code exists but place name is empty
        let placeName = branchData.placeName || "";
        if (branchData.placeCode && !placeName) {
          placeName = await fetchPlaceNameByCode(branchData.placeCode);
        }
        
        setFormData({
          branchID: branchData.branchID || "",
          forBranchID: branchData.forBranchID || "",
          branchCode: branchData.branchCode || "",
          branchName: branchData.branchName || "",
          creditLimit: branchData.creditLimit || 0,
          creditDays: branchData.creditDays || 0,
          enhancedCreditLimitPC: branchData.enhancedCreditLimitPC || 0,
          enhancedCreditLimitDays: branchData.enhancedCreditLimitDays || 0,
          beatID: branchData.beatID || "",
          branchType: branchData.branchType || "",
          entityCode: branchData.entityCode || "",
          stateCode: branchData.stateCode || "",
          gstNumber: branchData.gstNumber || "",
          rtype: branchData.rtype || "",
          zoneCode: branchData.zoneCode || "",
          createdUserId: branchData.createdUserId || "",
          mobile: branchData.mobile || "",
          houseNo: branchData.houseNo || "",
          building: branchData.building || "",
          branchAddress: branchData.branchAddress || "",
          landmark: branchData.landmark || "",
          cityCode: branchData.cityCode || "",
          placeCode: branchData.placeCode || "",
          placeName: placeName, // Use the fetched place name
          isDistanceEdited: branchData.isDistanceEdited || false,
          kmDistance: branchData.kmDistance || 0,
          pinCode: branchData.pinCode || "",
          altPhone: branchData.altPhone || "",
          email: branchData.email || "",
          foodLicenseNumber: branchData.foodLicenseNumber || "",
          latitude: branchData.latitude || 0,
          longitude: branchData.longitude || 0,
          createdDate: branchData.createdDate || "",
          modifiedUserId: branchData.modifiedUserId || "",
          modifiedDate: branchData.modifiedDate || "",
          deletedUserId: branchData.deletedUserId || "",
          deletedDate: branchData.deletedDate || "",
          // Additional fields from API
          isDefaultAddress: branchData.isDefaultAddress || false,
          addressID: branchData.addressID || ""
        });

        // Fetch places for the existing pin code
        if (branchData.pinCode && branchData.pinCode.length === 6) {
          fetchPlacesByPinCode(branchData.pinCode);
        }
      }
    } catch (error) {
      console.error("Error fetching branch details:", error);
      setError("Failed to fetch branch details. Please try again later.");
    }
  };

  const handleEdit = async (rowData, index) => {
    setIsEditing(true);
    setSelectedRowIndex(index);
    setFormErrors({});
    
    // Show loading state
    setLoading(true);
    
    try {
      // Fetch detailed branch data using the API
      await fetchBranchDetails(rowData.branchID);
    } catch (error) {
      console.error("Error in handleEdit:", error);
      // Fallback to rowData if API fails
      setFormData(rowData);
      
      // Fetch places for the existing pin code as fallback
      if (rowData.pinCode && rowData.pinCode.length === 6) {
        fetchPlacesByPinCode(rowData.pinCode);
      }
    } finally {
      setLoading(false);
      setOpenForm(true);
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setIsEditing(false);
    setSelectedRowIndex(null);
    setPlaceOptions([]);
  };

  const handleSaveForm = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      console.log("Form validation failed:", formErrors);
      return;
    }

    try {
      const token = localStorage.getItem('AuthorizationToken');
      const url = `${local_url}/api/Company/UpdateBranch`;

      console.log("API Request Body:", JSON.stringify(formData, null, 2));

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log("API Response:", responseData);

      await fetchLedgerData();
      handleCloseForm();
    } catch (error) {
      setError("Failed to update branch data. Please try again later.");
      console.error("API Error:", error);
    }
  };

  return (
    <div className="reports-root">
      <Header />
      <div className="reports-container">
        <div className="reports-header">
          <h1 className="reports-title">Branch List</h1>
        </div>

        {error && (
          <div className="reports-error">
            <span className="reports-error-text">{error}</span>
          </div>
        )}

        <div className="reports-table-wrapper">
          <div className="reports-table-container">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Branch ID</th>
                  <th>For Branch ID</th>
                  <th>Branch Code</th>
                  <th>Branch Name</th>
                  <th>Branch Type</th>
                  <th>Entity Code</th>
                  <th>State Code</th>
                  <th>GST Number</th>
                  <th>Zone Code</th>
                  <th>Credit Limit</th>
                  <th>Credit Days</th>
                  <th>Enhanced Credit Limit PC</th>
                  <th>Enhanced Credit Limit Days</th>
                  <th>Beat ID</th>
                  <th>RType</th>
                  <th>Mobile</th>
                  <th>Address</th>
                  <th>Pin Code</th>
                  <th>Email</th>
                  <th>Created Date</th>
                  <th>Modified Date</th>
                  <th className="sticky-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="23" className="reports-loading">
                      <CircularProgress size={40} />
                    </td>
                  </tr>
                ) : ledgerData.length > 0 ? (
                  ledgerData.map((item, index) => (
                    <tr key={index}>
                      <td style={{ textAlign: 'center' }}>{index + 1}</td>
                      <td>{item.branchID || "-"}</td>
                      <td>{item.forBranchID || "-"}</td>
                      <td>{item.branchCode || "-"}</td>
                      <td>{item.branchName || "-"}</td>
                      <td>{item.branchType || "-"}</td>
                      <td>{item.entityCode || "-"}</td>
                      <td>{item.stateCode || "-"}</td>
                      <td>{item.gstNumber || "-"}</td>
                      <td>{item.zoneCode || "-"}</td>
                      <td>{item.creditLimit || "-"}</td>
                      <td>{item.creditDays || "-"}</td>
                      <td>{item.enhancedCreditLimitPC || "-"}</td>
                      <td>{item.enhancedCreditLimitDays || "-"}</td>
                      <td>{item.beatID || "-"}</td>
                      <td>{item.rtype || "-"}</td>
                      <td>{item.mobile || "-"}</td>
                      <td>{item.branchAddress || "-"}</td>
                      <td>{item.pinCode || "-"}</td>
                      <td>{item.email || "-"}</td>
                      <td>{item.createdDate ? new Date(item.createdDate).toLocaleDateString() : "-"}</td>
                      <td>{item.modifiedDate ? new Date(item.modifiedDate).toLocaleDateString() : "-"}</td>
                      <td className="sticky-column reports-actions">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleEdit(item, index)}
                          disabled={loading}
                        >
                          {loading && selectedRowIndex === index ? (
                            <CircularProgress size={16} />
                          ) : (
                            'Edit'
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="23" className="reports-no-data">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={openForm} onClose={handleCloseForm} maxWidth="lg" fullWidth>
          <DialogTitle>{isEditing ? 'Edit Branch' : 'Add New Branch'}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', padding: '16px' }}>
              {/* Basic Information */}
              <TextField
                label="Branch ID *"
                variant="outlined"
                name="branchID"
                value={formData.branchID}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.branchID}
                helperText={formErrors.branchID}
              />
              <TextField
                label="For Branch ID *"
                variant="outlined"
                name="forBranchID"
                value={formData.forBranchID}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.forBranchID}
                helperText={formErrors.forBranchID}
              />
              <TextField
                label="Branch Code *"
                variant="outlined"
                name="branchCode"
                value={formData.branchCode}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.branchCode}
                helperText={formErrors.branchCode}
              />
              <TextField
                label="Branch Name *"
                variant="outlined"
                name="branchName"
                value={formData.branchName}
                onChange={handleFormChange}
                fullWidth
                disabled
                required
                error={!!formErrors.branchName}
                helperText={formErrors.branchName}
              />

              {/* Branch Type Dropdown */}
              <FormControl fullWidth variant="outlined" error={!!formErrors.branchType}>
                <InputLabel>Branch Type</InputLabel>
                <Select
                  value={formData.branchType}
                  onChange={(e) => handleFormChange({ target: { name: "branchType", value: e.target.value } })}
                  label="Branch Type"
                  name="branchType"
                >
                  {branchTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.branchType && <span style={{ color: '#d32f2f', fontSize: '0.75rem' }}>{formErrors.branchType}</span>}
              </FormControl>

              {/* RType Dropdown */}
              <FormControl fullWidth variant="outlined" error={!!formErrors.rtype}>
                <InputLabel>RType</InputLabel>
                <Select
                  value={formData.rtype}
                  onChange={(e) => handleFormChange({ target: { name: "rtype", value: e.target.value } })}
                  label="RType"
                  name="rtype"
                >
                  {rtypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.rtype && <span style={{ color: '#d32f2f', fontSize: '0.75rem' }}>{formErrors.rtype}</span>}
              </FormControl>

              {/* Company Code Dropdown */}
              <FormControl fullWidth variant="outlined" error={!!formErrors.entityCode}>
                <InputLabel>Company Code</InputLabel>
                <Select
                  name="entityCode"
                  value={formData.entityCode}
                  onChange={handleFormChange}
                  label="Company Code"
                >
                  {companyList.map((company) => (
                    <MenuItem key={company.entityCode} value={company.entityCode}>
                      {company.entityName} ({company.entityCode})
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.entityCode && <span style={{ color: '#d32f2f', fontSize: '0.75rem' }}>{formErrors.entityCode}</span>}
              </FormControl>

              {/* State Code Dropdown */}
              <FormControl fullWidth variant="outlined" error={!!formErrors.stateCode}>
                <InputLabel>State Code</InputLabel>
                <Select
                  value={formData.stateCode}
                  onChange={(e) => handleFormChange({ target: { name: "stateCode", value: e.target.value } })}
                  label="State Code"
                  name="stateCode"
                >
                  {stateOptions.map((state) => (
                    <MenuItem key={state.code} value={state.code}>
                      {state.name} ({state.code})
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.stateCode && <span style={{ color: '#d32f2f', fontSize: '0.75rem' }}>{formErrors.stateCode}</span>}
              </FormControl>

              <TextField
                label="GST Number"
                variant="outlined"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleFormChange}
                fullWidth
              />

              {/* Contact Information */}
              <TextField
                label="Mobile *"
                variant="outlined"
                name="mobile"
                value={formData.mobile}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.mobile}
                helperText={formErrors.mobile}
                inputProps={{ maxLength: 10 }}
              />

              <TextField
                label="Alternative Phone"
                variant="outlined"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleFormChange}
                fullWidth
              />

              <TextField
                label="Email"
                variant="outlined"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                fullWidth
                error={!!formErrors.email}
                helperText={formErrors.email}
              />

              {/* Address Information */}
              <TextField
                label="House No *"
                variant="outlined"
                name="houseNo"
                value={formData.houseNo}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.houseNo}
                helperText={formErrors.houseNo}
              />

              <TextField
                label="Building *"
                variant="outlined"
                name="building"
                value={formData.building}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.building}
                helperText={formErrors.building}
              />

              <TextField
                label="Branch Address *"
                variant="outlined"
                name="branchAddress"
                value={formData.branchAddress}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={2}
                required
                error={!!formErrors.branchAddress}
                helperText={formErrors.branchAddress}
              />

              <TextField
                label="Landmark *"
                variant="outlined"
                name="landmark"
                value={formData.landmark}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.landmark}
                helperText={formErrors.landmark}
              />

              <TextField
                label="City Code *"
                variant="outlined"
                name="cityCode"
                value={formData.cityCode}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.cityCode}
                helperText={formErrors.cityCode}
              />

              {/* Pin Code with Places Integration */}
              <TextField
                label="Pin Code *"
                variant="outlined"
                name="pinCode"
                value={formData.pinCode}
                onChange={handleFormChange}
                fullWidth
                required
                error={!!formErrors.pinCode}
                helperText={formErrors.pinCode}
                inputProps={{ maxLength: 6 }}
              />

              {/* Place Selection with Autocomplete */}
              <Autocomplete
                options={placeOptions}
                getOptionLabel={(option) => `${option.placeName} (${option.placeId})`}
                value={placeOptions.find(place => place.placeId === formData.placeCode) || null}
                onChange={handlePlaceSelect}
                loading={loadingPlaces}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Place *"
                    variant="outlined"
                    required
                    error={!!formErrors.placeName}
                    helperText={formErrors.placeName}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPlaces ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={!formData.pinCode || formData.pinCode.length !== 6}
              />

              <TextField
                label="Place Name *"
                variant="outlined"
                name="placeName"
                value={formData.placeName}
                onChange={handleFormChange}
                fullWidth
                required
                disabled
                error={!!formErrors.placeName}
                helperText={formErrors.placeName}
              />

              {/* Location Information */}
              <TextField
                label="Latitude"
                variant="outlined"
                name="latitude"
                type="number"
                value={formData.latitude}
                onChange={handleFormChange}
                fullWidth
                inputProps={{ step: "any" }}
              />

              <TextField
                label="Longitude"
                variant="outlined"
                name="longitude"
                type="number"
                value={formData.longitude}
                onChange={handleFormChange}
                fullWidth
                inputProps={{ step: "any" }}
              />

              <TextField
                label="KM Distance"
                variant="outlined"
                name="kmDistance"
                type="number"
                value={formData.kmDistance}
                onChange={handleFormChange}
                fullWidth
              />

              {/* Credit Information */}
              <TextField
                label="Credit Limit"
                variant="outlined"
                name="creditLimit"
                type="number"
                value={formData.creditLimit}
                onChange={handleFormChange}
                fullWidth
              />

              <TextField
                label="Credit Days"
                variant="outlined"
                name="creditDays"
                type="number"
                value={formData.creditDays}
                onChange={handleFormChange}
                fullWidth
              />

              <TextField
                label="Enhanced Credit Limit PC"
                variant="outlined"
                name="enhancedCreditLimitPC"
                type="number"
                value={formData.enhancedCreditLimitPC}
                onChange={handleFormChange}
                fullWidth
              />

              <TextField
                label="Enhanced Credit Limit Days"
                variant="outlined"
                name="enhancedCreditLimitDays"
                type="number"
                value={formData.enhancedCreditLimitDays}
                onChange={handleFormChange}
                fullWidth
              />

              <TextField
                label="Beat ID"
                variant="outlined"
                name="beatID"
                value={formData.beatID}
                onChange={handleFormChange}
                fullWidth
              />

              <TextField
                label="Zone Code"
                variant="outlined"
                name="zoneCode"
                value={formData.zoneCode}
                onChange={handleFormChange}
                fullWidth
              />

              <TextField
                label="Food License Number"
                variant="outlined"
                name="foodLicenseNumber"
                value={formData.foodLicenseNumber}
                onChange={handleFormChange}
                fullWidth
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSaveForm} color="primary" variant="contained">
              {isEditing ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      <style jsx>{`
        .reports-root {
          min-height: 100vh;
          background-color: #f5f7fa;
        }

        .reports-container {
          padding: 1rem;
          max-width: 1440px;
          margin: 0 auto;
        }

        .reports-header {
          display: flex;
          justify-content: space-around;
          align-items: center;
          margin-bottom: 2rem;
        }

        .reports-title {
          font-size: 1.75rem;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }

        .dropdown-and-button {
          display: flex;
          align-items: center;
        }

        .reports-error {
          background-color: #fee2e2;
          border: 1px solid #ef4444;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }

        .reports-error-text {
          color: #dc2626;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .reports-table-wrapper {
          background-color: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
                      0 2px 4px -1px rgba(0, 0, 0, 0.06);
          overflow: hidden;
        }
            .reports-table-container {
              overflow-x: auto;
              position: relative;
            }
  
            .reports-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
              font-size: 0.875rem;
            }
  
            .reports-table th {
              background-color: #f8f9fa;
              color: #4b5563;
              font-weight: 600;
              text-align: left;
              padding: 1rem;
              border-bottom: 2px solid #e5e7eb;
              white-space: nowrap;
            }
  
            .reports-table td {
              padding: 1rem;
              border-bottom: 1px solid #e5e7eb;
              color: #374151;
            }
  
            .reports-table tbody tr:hover {
              background-color: #f9fafb;
            }
  
            .sticky-column {
              position: sticky !important;
              right: 0;
              background-color: white !important;
              z-index: 20;
              box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
            }
  
            .reports-table tbody tr:hover .sticky-column {
              background-color: #f9fafb !important;
            }
  
            .reports-table th.sticky-column {
              background-color: #f8f9fa !important;
              z-index: 21;
            }
  
            .reports-actions {
              white-space: nowrap;
              text-align: center;
              min-width: 100px;
              padding: 0.5rem !important;
            }
  
            .reports-loading {
              text-align: center;
              padding: 3rem !important;
          }

          .reports-no-data {
            text-align: center;
            color: #6b7280;
            font-style: italic;
            padding: 2rem !important;
          }

          .form-row {
            display: flex;
            gap: 1rem;
            margin-bottom: 1rem;
          }

          .form-row .MuiTextField-root {
            flex: 1;
          }

          @media (max-width: 1024px) {
            .reports-container {
              padding: 0.5rem;
            }

            .reports-header {
              flex-direction: column;
              gap: 1rem;
            }

            .reports-title {
              font-size: 1.5rem;
              text-align: center;
            }

            .form-row {
              flex-direction: column;
              gap: 0;
            }
          }

          @media (max-width: 768px) {
            .reports-table-container {
              font-size: 0.75rem;
            }

            .reports-table th,
            .reports-table td {
              padding: 0.75rem;
            }
          }
        `}</style>
    </div>
  );
};

export default BranchList;